"use client";

/**
 * Subscribes to the effective subscription state for the active household.
 *
 * Source of truth is the server-side `getEffectiveSubscription` callable:
 * it computes trial state from the household's Firestore-native createTime,
 * resolves stored Stripe coverage, etc. The client never derives trial on
 * its own. See [[new-accounts-hit-paywall-instead-of-getting-30-day]].
 *
 * Real-time pattern: we keep an `onSnapshot` listener on the household doc
 * as a "something changed" signal — every snapshot triggers a re-fetch via
 * the callable. The listener payload itself is ignored. This way Stripe
 * webhook writes to `subscription` field cause the UI to refresh within
 * seconds, but the effective state always comes from the server function.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "@/lib/firebase";
import getApp from "@/lib/firebase";
import { useAuthContext } from "@/contexts/AuthContext";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import type { HouseholdSubscription } from "@/lib/models";
import {
  deriveSubscriptionState,
  type SubscriptionState,
} from "@/lib/subscription";

interface UseHouseholdSubscriptionResult {
  state: SubscriptionState;
  loading: boolean;
}

export function useHouseholdSubscription(): UseHouseholdSubscriptionResult {
  const { user } = useAuthContext();
  const { householdId } = useHouseholdContext();

  const [subscription, setSubscription] = useState<
    HouseholdSubscription | undefined
  >(undefined);
  const [hasStripeCustomer, setHasStripeCustomer] = useState<
    boolean | undefined
  >(undefined);
  const [subLoaded, setSubLoaded] = useState(false);
  const [custLoaded, setCustLoaded] = useState(false);

  // Track the latest call so racey returns from earlier fetches don't
  // overwrite a more recent result.
  const fetchSeqRef = useRef(0);

  const fetchEffective = useCallback(
    async (forHouseholdId: string) => {
      const seq = ++fetchSeqRef.current;
      try {
        const functions = getFunctions(getApp());
        const callable = httpsCallable<
          { household_id: string },
          { subscription: HouseholdSubscription }
        >(functions, "getEffectiveSubscription");
        const result = await callable({ household_id: forHouseholdId });
        if (seq === fetchSeqRef.current) {
          setSubscription(result.data?.subscription);
          setSubLoaded(true);
        }
      } catch (err) {
        // On error (rules/auth/network), fall back to "no subscription" so
        // the UI doesn't hang on loading. Not fatal — the next snapshot
        // event will retry.
        console.warn("getEffectiveSubscription failed", err);
        if (seq === fetchSeqRef.current) {
          setSubscription(undefined);
          setSubLoaded(true);
        }
      }
    },
    [],
  );

  // Listen on the household doc as a "something changed" signal. Each
  // snapshot triggers a re-fetch of the effective subscription via the
  // callable. Snapshot data itself is ignored — server-side calc wins.
  useEffect(() => {
    if (!householdId) {
      setSubscription(undefined);
      setSubLoaded(true);
      return;
    }
    setSubLoaded(false);
    const ref = doc(db, "households", householdId);
    const unsub = onSnapshot(
      ref,
      () => {
        void fetchEffective(householdId);
      },
      () => {
        // Snapshot listener errored — try one direct fetch so we have
        // some state instead of spinning forever.
        void fetchEffective(householdId);
      },
    );
    return unsub;
  }, [householdId, fetchEffective]);

  // stripe_customers/{uid} listener — single source of truth for
  // "has this user ever subscribed?"
  useEffect(() => {
    if (!user?.uid) {
      setHasStripeCustomer(undefined);
      setCustLoaded(true);
      return;
    }
    const ref = doc(db, "stripe_customers", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setHasStripeCustomer(snap.exists());
        setCustLoaded(true);
      },
      () => {
        // Rules may deny reads if the doc doesn't exist yet — treat as "no".
        setHasStripeCustomer(false);
        setCustLoaded(true);
      },
    );
    return unsub;
  }, [user?.uid]);

  const state = deriveSubscriptionState(subscription, {
    hasStripeCustomer,
  });

  return {
    state,
    loading: !subLoaded || !custLoaded,
  };
}
